import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography, Button, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import type { Proposal } from "../types/proposal.types";
import type { CoverPageTemplate } from "../types/cover.types";
import CoverPage from "./CoverPage";
import MainProposalPage from "./MainProposalPage";
import AgreementFormPage from "./AgreementForm";
import ProposalServicesPage from "./ProposalServices";
import ServicePage from "./AdditionalServicesPage";
import WhatsAppPreviewPage from "./WhatsMessagePreviewPage";
import MemorialPage from "./MemorialPreviewPage";
import ProposalLetterTemplate from "./ProposalLetterHead";
import CoverPageTemplateModal from "./components/CoverPageTemplateModal";
import MuzukuruLogo from "../../../assets/muzukuru.png";
import letterheadpattern from "../assets/letterheadpattern.png";
import { generateProposalPDF } from "../utils/pdf-generator";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import apiService from "../../../service/Api";

const PROPOSAL_PAGES = [
  { id: "cover", label: "Cover Page" },
  { id: "main", label: "Letter & Overview" },
  { id: "services", label: "Included Services" },
  { id: "service", label: "Additional Services" },
  { id: "whatsapp", label: "WhatsApp Message" },
  { id: "stream", label: "Memorial Page" },
  { id: "agreement", label: "Agreement Form" },
];
const PdfPage: React.FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => (
  <Paper
    id={id}
    sx={{
      width: "210mm",
      minHeight: "297mm",
      boxSizing: "border-box",
      backgroundColor: "#ffffff",
      boxShadow: "none",
      overflow: "hidden",
      pageBreakAfter: "always",
    }}
  >
    {children}
  </Paper>
);

interface ProposalPreviewProps {
  proposal: Proposal;
  onBack: () => void;
  selectedTemplate: CoverPageTemplate | null;
  onTemplateChange: (template: CoverPageTemplate) => void;
  onProposalUpdate?: (updatedProposal: Proposal) => void;
}

const ProposalPreview: React.FC<ProposalPreviewProps> = ({
  proposal,
  onBack,
  selectedTemplate,
  onTemplateChange,
  onProposalUpdate,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [activePage, setActivePage] = useState("cover");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const sidebarRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const allRefsReady = PROPOSAL_PAGES.every(
      (page) => pageRefs.current[page.id]
    );
    if (!allRefsReady) return;

    const observerOptions = {
      root: scrollContainerRef.current,
      threshold: [0, 0.25, 0.5, 0.75, 1],
      rootMargin: "-100px 0px -60% 0px",
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      let maxRatio = 0;
      let mostVisiblePageId = "";

      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          const pageId = entry.target.getAttribute("data-page-id");
          if (pageId) {
            mostVisiblePageId = pageId;
          }
        }
      });

      if (mostVisiblePageId && maxRatio > 0) {
        setActivePage(mostVisiblePageId);
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    Object.entries(pageRefs.current).forEach(([_, ref]) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [proposal]);

  useEffect(() => {
    const activeThumbnail = thumbnailRefs.current[activePage];
    const sidebar = sidebarRef.current;

    if (activeThumbnail && sidebar) {
      const thumbnailTop = activeThumbnail.offsetTop;
      const thumbnailHeight = activeThumbnail.offsetHeight;
      const sidebarScrollTop = sidebar.scrollTop;
      const sidebarHeight = sidebar.clientHeight;

      // Check if thumbnail is out of view
      const isAboveView = thumbnailTop < sidebarScrollTop;
      const isBelowView =
        thumbnailTop + thumbnailHeight > sidebarScrollTop + sidebarHeight;

      if (isAboveView || isBelowView) {
        const scrollPosition =
          thumbnailTop - sidebarHeight / 2 + thumbnailHeight / 2;
        sidebar.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  }, [activePage]);

  const handleThumbnailClick = (pageId: string) => {
    const pageElement = pageRefs.current[pageId];
    if (pageElement && scrollContainerRef.current) {
      pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderPageThumbnail = (pageId: string) => {
    switch (pageId) {
      case "cover":
        return <CoverPage proposal={proposal} template={selectedTemplate} />;
      case "main":
        return (
          <>
            <ProposalLetterTemplate
              companyInfo={{
                name: "Muzukuru Media",
                address: "46 Mt Pleasant Drive, Harare",
                phone: "+263 771 453 985",
                email: "admin@muzukuru.com",
                logo: MuzukuruLogo,
              }}
              backgroundPattern={letterheadpattern}
            />
            <MainProposalPage proposal={proposal} />
          </>
        );
      case "services":
        return <ProposalServicesPage proposal={proposal} />;
      case "service":
        return <ServicePage proposal={proposal} />;
      case "whatsapp":
        return <WhatsAppPreviewPage proposal={proposal} />;
      case "stream":
        return <MemorialPage proposal={proposal} />;
      case "agreement":
        return <AgreementFormPage proposal={proposal} />;
      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      await generateProposalPDF(proposal);
      showSuccessToast("PDF downloaded successfully!");
    } catch {
      showErrorToast("Error generating PDF");
    }
  };

  const handleSendEmail = async () => {
    try {
      setIsSendingEmail(true);

      let proposalId = proposal.id;
      if (!proposalId) {
        await handleSave();
        proposalId = proposal.id;
        if (!proposalId) return;
      }

      const response = await apiService.post(
        `/api/bookings/booking-proposal/${proposalId}/send-email/`
      );

      if (response.status === 200 || response.status === 201) {
        showSuccessToast(
          `Email sent successfully to ${proposal.client.email}!`
        );
      } else {
        showErrorToast("Failed to send email.");
      }
    } catch (err: any) {
      showErrorToast(err.response?.data?.message || "Error sending email.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const payload: Partial<Proposal> = {
        ...proposal,
        status: "final",
        updatedAt: new Date().toISOString(),
      };

      const response = proposal.id
        ? await apiService.put<Proposal>(
            `/api/bookings/booking-proposal/${proposal.id}/`,
            payload
          )
        : await apiService.post<Proposal>("/api/bookings/booking-proposal/", {
            ...payload,
            createdAt: new Date().toISOString(),
            validUntil: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(),
          });

      if (response.status === 200 || response.status === 201) {
        onProposalUpdate?.(response.data);
        showSuccessToast("Proposal saved successfully!");
      } else {
        showErrorToast("Failed to save proposal.");
      }
    } catch {
      showErrorToast("Error saving proposal.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <CoverPageTemplateModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={(template) => {
          onTemplateChange(template);
          setShowTemplateModal(false);
        }}
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Proposal Preview</Typography>
        </Box>

        <Box>
          <Button
            startIcon={<SwapHorizIcon />}
            variant="outlined"
            sx={{ mr: 1, borderColor: "#555", border: 2 }}
            onClick={() => setShowTemplateModal(true)}
          >
            Change Template
          </Button>

          <Button
            startIcon={<EmailIcon />}
            variant="outlined"
            sx={{ mr: 1, borderColor: "#555", border: 2 }}
            onClick={handleSendEmail}
            disabled={isSendingEmail}
          >
            {isSendingEmail ? "Sending..." : "Send Email"}
          </Button>

          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            sx={{ mr: 1, borderColor: "#555", border: 2 }}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>

          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isSaving}
            sx={{ bgcolor: reduxTheme.primary_colors[400] }}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : proposal.id ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: "flex", height: "calc(100vh - 80px)" }}>
        <Box
          ref={sidebarRef}
          sx={{
            width: 200,

            overflowY: "auto",
            p: 2,
          }}
        >
          {PROPOSAL_PAGES.map((page, index) => (
            <Box
              component="div"
              key={page.id}
              ref={(el: HTMLDivElement | null) => {
                thumbnailRefs.current[page.id] = el;
              }}
              onClick={() => handleThumbnailClick(page.id)}
              sx={{
                mb: 2,
                cursor: "pointer",
                position: "relative",
                border:
                  activePage === page.id
                    ? "3px solid #614338"
                    : "3px solid transparent",
                borderRadius: 1,
                overflow: "hidden",
                transition: "all 0.2s ease",
                "&:hover": {
                  border: "3px solid #614338",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "8.5/11",
                  bgcolor: "#fff",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "816px",
                    minHeight: "1056px",
                    transform: "scale(0.21)",
                    transformOrigin: "top left",
                    pointerEvents: "none",
                  }}
                >
                  {renderPageThumbnail(page.id)}
                </Box>
              </Box>
              {/* Page Number */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "12px",
                  mt: 0.5,
                  mb: 0.5,
                }}
              >
                {index + 1}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Box sx={{ maxWidth: 816 }}>
            {/* Cover Page */}
            <Paper
              id="cover-page"
              data-page-id="cover"
              ref={(el) => {
                pageRefs.current["cover"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <CoverPage proposal={proposal} template={selectedTemplate} />
            </Paper>

            {/* Main Page */}
            <Paper
              id="main-page"
              data-page-id="main"
              ref={(el) => {
                pageRefs.current["main"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <ProposalLetterTemplate
                companyInfo={{
                  name: "Muzukuru Media",
                  address: "46 Mt Pleasant Drive, Harare",
                  phone: "+263 771 453 985",
                  email: "admin@muzukuru.com",
                  logo: MuzukuruLogo,
                }}
                backgroundPattern={letterheadpattern}
              />
              <MainProposalPage proposal={proposal} />
            </Paper>

            {/* Services Page */}
            <Paper
              id="services-page"
              data-page-id="services"
              ref={(el) => {
                pageRefs.current["services"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <ProposalServicesPage proposal={proposal} />
            </Paper>

            {/* Service Page */}
            <Paper
              id="service-page"
              data-page-id="service"
              ref={(el) => {
                pageRefs.current["service"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <ServicePage proposal={proposal} />
            </Paper>

            {/* WhatsApp Page */}
            <Paper
              id="whatsapp-page"
              data-page-id="whatsapp"
              ref={(el) => {
                pageRefs.current["whatsapp"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <WhatsAppPreviewPage proposal={proposal} />
            </Paper>

            {/* Stream Page */}
            <Paper
              id="stream-page"
              data-page-id="stream"
              ref={(el) => {
                pageRefs.current["stream"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <MemorialPage proposal={proposal} />
            </Paper>

            {/* Agreement Page */}
            <Paper
              id="agreement-page"
              data-page-id="agreement"
              ref={(el) => {
                pageRefs.current["agreement"] = el;
              }}
              sx={{
                width: 816,
                minHeight: 1056,
                mb: 3,
                boxShadow: 0,
              }}
            >
              <AgreementFormPage proposal={proposal} />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Box
        id="pdf-root"
        sx={{
          position: "fixed",
          top: "-10000px",
          left: "-10000px",
          width: "210mm",
          backgroundColor: "#ffffff",
          zIndex: -1,
        }}
      >
        <PdfPage id="cover-page-pdf">
          <CoverPage proposal={proposal} template={selectedTemplate} />
        </PdfPage>

        <PdfPage id="main-page-pdf">
          <ProposalLetterTemplate
            companyInfo={{
              name: "Muzukuru Media",
              address: "46 Mt Pleasant Drive, Harare",
              phone: "+263 771 453 985",
              email: "admin@muzukuru.com",
              logo: MuzukuruLogo,
            }}
            backgroundPattern={letterheadpattern}
          />
          <MainProposalPage proposal={proposal} />
        </PdfPage>

        <PdfPage id="services-page-pdf">
          <ProposalServicesPage proposal={proposal} />
        </PdfPage>

        <PdfPage id="service-page-pdf">
          <ServicePage proposal={proposal} />
        </PdfPage>

        <PdfPage id="whatsapp-page-pdf">
          <WhatsAppPreviewPage proposal={proposal} />
        </PdfPage>

        <PdfPage id="stream-page-pdf">
          <MemorialPage proposal={proposal} />
        </PdfPage>

        <PdfPage id="agreement-page-pdf">
          <AgreementFormPage proposal={proposal} />
        </PdfPage>
      </Box>
    </Box>
  );
};

export default ProposalPreview;
